import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '@/firebase/config';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { SidebarProvider } from '@/components/ui/sidebar';
import OwnerSidebar from './OwnerSidebar';
import OwnerHeader from './OwnerHeader';
import {
  MessageSquare,
  MessageCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  Paperclip,
  User,
  Mail,
  Phone,
  Building2,
  Calendar,
  Search,
  ExternalLink,
  ArrowLeft,
  TrendingUp,
  Loader2
} from 'lucide-react';

interface SupportTicket {
  id: string;
  name: string;
  email: string;
  phone: string;
  schoolName: string;
  role: string;
  category: string;
  subject: string;
  description: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  traceId: string;
  submittedBy: string;
  submitterEmail: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  attachments?: { name: string; url: string; type: string }[];
  responses?: { 
    id: string;
    message: string; 
    sender: string; 
    timestamp: Timestamp;
    isOwner: boolean;
  }[];
  systemInfo?: {
    userAgent: string;
    url: string;
    timestamp: string;
  };
}

const SupportTicketsManager = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [loading, setLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const { toast } = useToast();

  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedTicket(null);
    setResponseMessage('');
  };

  useEffect(() => {
    const ticketsQuery = query(
      collection(db, 'supportTickets'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(ticketsQuery, (snapshot) => {
      const ticketsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SupportTicket[];
      
      setTickets(ticketsData);
      setFilteredTickets(ticketsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching tickets:', error);
      toast({
        title: 'Error loading tickets',
        description: error.message,
        variant: 'destructive'
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [toast]);

  useEffect(() => {
    let filtered = tickets;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(ticket =>
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.schoolName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.traceId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTickets(filtered);
  }, [tickets, statusFilter, priorityFilter, searchQuery]);

  const handleUpdateStatus = async (ticketId: string, newStatus: string) => {
    try {
      const ticketRef = doc(db, 'supportTickets', ticketId);
      await updateDoc(ticketRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      toast({
        title: 'Status Updated',
        description: `Ticket status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Update Failed',
        description: 'Could not update ticket status',
        variant: 'destructive'
      });
    }
  };

  const handleUpdatePriority = async (ticketId: string, newPriority: string) => {
    try {
      const ticketRef = doc(db, 'supportTickets', ticketId);
      await updateDoc(ticketRef, {
        priority: newPriority,
        updatedAt: serverTimestamp()
      });

      toast({
        title: 'Priority Updated',
        description: `Ticket priority changed to ${newPriority}`,
      });
    } catch (error) {
      console.error('Error updating priority:', error);
      toast({
        title: 'Update Failed',
        description: 'Could not update ticket priority',
        variant: 'destructive'
      });
    }
  };

  const handleSendResponse = async () => {
    if (!selectedTicket || !responseMessage.trim()) return;

    setIsSending(true);
    try {
      const ticketRef = doc(db, 'supportTickets', selectedTicket.id);
      const newResponse = {
        id: `resp-${Date.now()}`,
        message: responseMessage,
        sender: 'Support Team',
        timestamp: serverTimestamp(),
        isOwner: true
      };

      const updatedResponses = [...(selectedTicket.responses || []), newResponse];
      
      await updateDoc(ticketRef, {
        responses: updatedResponses,
        status: 'in-progress',
        updatedAt: serverTimestamp()
      });

      setResponseMessage('');
      toast({
        title: 'Response Sent',
        description: 'Your response has been sent to the user',
      });
    } catch (error) {
      console.error('Error sending response:', error);
      toast({
        title: 'Send Failed',
        description: 'Could not send response',
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'low': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4" />;
      case 'in-progress': return <Clock className="h-4 w-4" />;
      case 'resolved': return <CheckCircle2 className="h-4 w-4" />;
      case 'closed': return <XCircle className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return timestamp.toDate().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900 w-full">
          <OwnerSidebar activeSection="Support & Tickets" onSectionChange={() => {}} userRole="owner" />
          <div className="flex-1 flex flex-col overflow-hidden">
            <OwnerHeader />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">Loading support tickets...</p>
            </div>
          </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900 w-full">
        <OwnerSidebar activeSection="Support & Tickets" onSectionChange={() => {}} userRole="owner" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <OwnerHeader />
        <div className="flex-1 overflow-auto">
          {viewMode === 'list' ? (
            <div className="p-6 space-y-6 max-w-7xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Support & Tickets</h1>
                  <p className="text-slate-600 dark:text-slate-400">Manage and respond to support requests from users</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => navigate('/owner/live-support')}
                    className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 border-0 text-white shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 active:scale-95"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Live School View
                  </Button>
                  <Badge variant="outline" className="text-sm px-3 py-1.5 h-10">
                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                    {stats.total} Total
                  </Badge>
                </div>
              </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Tickets</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
              </div>
              <MessageSquare className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Open</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{stats.open}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">{stats.inProgress}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Resolved</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.resolved}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by subject, name, email, school, or trace ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>Support Tickets ({filteredTickets.length})</CardTitle>
          <CardDescription>Click on a ticket to view details and respond</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">No tickets found</p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div key={ticket.id} className="border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900/50 transition">
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getStatusColor(ticket.status)}>
                            {getStatusIcon(ticket.status)}
                            <span className="ml-1.5">{ticket.status}</span>
                          </Badge>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                            #{ticket.id.slice(0, 8).toUpperCase()}
                          </span>
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 dark:text-white text-lg mb-1 truncate">
                          {ticket.subject}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {ticket.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" />
                            {ticket.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3.5 w-3.5" />
                            {ticket.schoolName}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(ticket.createdAt)}
                          </span>
                        </div>
                      </div>

                      <Button 
                        size="sm" 
                        onClick={() => handleViewTicket(ticket)}
                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
            </div>
          ) : (
            /* TICKET DETAIL VIEW */
            <div className="p-8 space-y-6 max-w-6xl mx-auto">
              {/* Back Button & Header */}
              <div className="flex items-center gap-4 mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackToList}
                  className="gap-2 border-slate-200 dark:border-slate-700"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tickets
                </Button>
                <div className="flex-1">
                  <div className="flex items-center gap-2.5">
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                      Ticket #{selectedTicket?.id.slice(0, 8).toUpperCase()}
                    </h1>
                    {selectedTicket && (
                      <>
                        <Badge className={`${getStatusColor(selectedTicket.status)} border font-medium`}>
                          {getStatusIcon(selectedTicket.status)}
                          <span className="ml-1.5 capitalize">{selectedTicket.status.replace('-', ' ')}</span>
                        </Badge>
                        <Badge className={`${getPriorityColor(selectedTicket.priority)} border font-medium`}>
                          <span className="capitalize">{selectedTicket.priority}</span>
                        </Badge>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Submitted on {selectedTicket && formatDate(selectedTicket.createdAt)}
                  </p>
                </div>
              </div>

              {selectedTicket && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Details */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Status & Priority Controls */}
                    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-base">Ticket Management</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Status</label>
                            <Select 
                              value={selectedTicket.status} 
                              onValueChange={(value) => handleUpdateStatus(selectedTicket.id, value)}
                            >
                              <SelectTrigger className="border-slate-200 dark:border-slate-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in-progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Priority</label>
                            <Select 
                              value={selectedTicket.priority} 
                              onValueChange={(value) => handleUpdatePriority(selectedTicket.id, value)}
                            >
                              <SelectTrigger className="border-slate-200 dark:border-slate-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Issue Details */}
                    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-base">Issue Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Category</p>
                          <Badge className="capitalize bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            {selectedTicket.category}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Subject</p>
                          <p className="text-base font-medium text-slate-900 dark:text-white">{selectedTicket.subject}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Description</p>
                          <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                              {selectedTicket.description}
                            </p>
                          </div>
                        </div>
                        {selectedTicket.traceId && (
                          <div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Trace ID</p>
                            <code className="text-xs bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-md font-mono text-slate-700 dark:text-slate-300">
                              {selectedTicket.traceId}
                            </code>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Attachments */}
                    {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                      <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Paperclip className="h-4 w-4" />
                            Attachments ({selectedTicket.attachments.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {selectedTicket.attachments.map((file, idx) => (
                              <a
                                key={idx}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group"
                              >
                                <Paperclip className="h-4 w-4 text-slate-400 group-hover:text-blue-500" />
                                <span className="text-sm flex-1 truncate text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                  {file.name}
                                </span>
                                <ExternalLink className="h-4 w-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </a>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Conversation */}
                    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-base">Conversation</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4 mb-6">
                          {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                            selectedTicket.responses.map((response) => (
                              <div
                                key={response.id}
                                className={`p-4 rounded-xl border ${
                                  response.isOwner
                                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 ml-8'
                                    : 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 mr-8'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {response.sender}
                                  </span>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {response.timestamp && formatDate(response.timestamp)}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                                  {response.message}
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                              No responses yet. Be the first to respond to this ticket.
                            </p>
                          )}
                        </div>

                        <div className="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-6">
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block">
                            Send Response
                          </label>
                          <Textarea
                            placeholder="Type your response here..."
                            value={responseMessage}
                            onChange={(e) => setResponseMessage(e.target.value)}
                            className="min-h-[120px] border-slate-200 dark:border-slate-700 resize-none"
                          />
                          <Button
                            onClick={handleSendResponse}
                            disabled={isSending || !responseMessage.trim()}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                          >
                            {isSending ? (
                              <>
                                <Clock className="mr-2 h-4 w-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="mr-2 h-4 w-4" />
                                Send Response
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Contact Info */}
                  <div className="space-y-6">
                    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-base">Contact Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Name</p>
                            <p className="text-sm font-medium text-slate-900 dark:text-white mt-0.5">
                              {selectedTicket.name}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                            <a 
                              href={`mailto:${selectedTicket.email}`}
                              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline mt-0.5 block"
                            >
                              {selectedTicket.email}
                            </a>
                          </div>
                        </div>

                        {selectedTicket.phone && (
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                              <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-slate-500 dark:text-slate-400">Phone</p>
                              <a 
                                href={`tel:${selectedTicket.phone}`}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline mt-0.5 block"
                              >
                                {selectedTicket.phone}
                              </a>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400">School</p>
                            <p className="text-sm font-medium text-slate-900 dark:text-white mt-0.5">
                              {selectedTicket.schoolName}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Role</p>
                            <p className="text-sm font-medium text-slate-900 dark:text-white capitalize mt-0.5">
                              {selectedTicket.role}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {selectedTicket.systemInfo && (
                      <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-sm">
                        <CardHeader>
                          <CardTitle className="text-base">System Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">User Agent</p>
                            <p className="text-xs font-mono text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-200 dark:border-slate-700">
                              {selectedTicket.systemInfo.userAgent}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Page URL</p>
                            <p className="text-xs font-mono text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-200 dark:border-slate-700 break-all">
                              {selectedTicket.systemInfo.url}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SupportTicketsManager;
